import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PlusCircle, ArrowRight, Crown } from "lucide-react";
import {
  myAdStatsQuery,
  myAdsQuery,
  myPlanQuery,
  myProfileQuery,
  inboxQuery,
  myBidsQuery,
} from "@/lib/queries";
import { PLANS, planAtLeast, remainingThisMonth, type PlanId, type AdUsage } from "@/lib/plans";
import { daysLeft, type AdStatRow } from "@/lib/ad-stats";
import { Button } from "@/components/ui/button";
import { DashboardStats } from "@/components/dashboard/DashboardStats";
import { DashboardAdList } from "@/components/dashboard/DashboardAdList";
import { DashboardTodo, buildTodos } from "@/components/dashboard/DashboardTodo";
import { DashboardBids } from "@/components/dashboard/DashboardBids";
import { PushPromptDialog } from "@/components/PushPromptDialog";
import { ScanCompleteCard } from "@/components/scan/ScanCompleteCard";
import { listMyScans } from "@/lib/scan-runs.functions";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "@/lib/i18n";


export const Route = createFileRoute("/_authenticated/oversikt")({
  head: () => ({
    meta: [
      { title: "Min översikt — Sellavo" },
      {
        name: "description",
        content:
          "Din översikt på Sellavo: aktiva annonser, visningar, kontakter och vad som behöver din uppmärksamhet.",
      },
      { property: "og:title", content: "Min översikt — Sellavo" },
      {
        property: "og:description",
        content: "Aktiva annonser, visningar och kontakter samlat på ett ställe.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(myAdStatsQuery()),
      context.queryClient.ensureQueryData(myAdsQuery()),
      context.queryClient.ensureQueryData(myPlanQuery()),
      context.queryClient.ensureQueryData(inboxQuery()),
      context.queryClient.ensureQueryData(myBidsQuery()),
    ]);
  },
  component: OverviewPage,
});

function OverviewPage() {
  const { t } = useTranslation();
  const fetchScans = useServerFn(listMyScans);
  const { data: scans } = useQuery({ queryKey: ["my-scans"], queryFn: () => fetchScans({}) });
  const { data: stats, isLoading } = useQuery(myAdStatsQuery());
  const { data: ads } = useQuery(myAdsQuery());
  const { data: planStatus } = useQuery(myPlanQuery());
  const { data: profile } = useQuery(myProfileQuery());
  const { data: inbox } = useQuery(inboxQuery());
  const { data: bids } = useQuery(myBidsQuery());

  const rows: AdStatRow[] = stats?.ads ?? [];
  const activeRows = rows.filter((r) => !r.is_paused && daysLeft(r.expires_at) > 0);
  const views = rows.reduce((sum, r) => sum + r.views, 0);
  const contacts = rows.reduce((sum, r) => sum + r.contacts, 0);
  const unread = (inbox ?? []).reduce((sum, t) => sum + (t.unread ?? 0), 0);

  const images = new Map<string, string>();
  for (const ad of (ads ?? []) as Array<{ slug: string; image_url?: string | null }>) {
    if (ad.image_url) images.set(ad.slug, ad.image_url);
  }

  const currentPlan: PlanId = (planStatus?.plan as PlanId) ?? "free";
  const plan = PLANS[currentPlan];
  const usage: AdUsage = planStatus?.usage ?? { day: 0, week: 0, month: 0 };
  const left = remainingThisMonth(currentPlan, usage);

  const firstName = (profile?.displayName ?? "").trim().split(" ")[0] || null;
  const todos = buildTodos(rows, unread, t);
  const topRows = [...rows]
    .sort((a, b) => Number(a.is_paused) - Number(b.is_paused) || b.views - a.views)
    .slice(0, 5);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <PushPromptDialog />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">

        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            {firstName ? t("account.overview.greeting", { name: firstName }) : t("account.overview.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground sm:mt-2 sm:text-base">
            {t("account.overview.intro")}
          </p>
        </div>
        <Button asChild className="gap-2">
          <Link to="/skapa">
            <PlusCircle className="h-4 w-4" /> {t("account.overview.create")}
          </Link>
        </Button>
      </div>

      <div className="mt-5 sm:mt-8">
        <ScanCompleteCard
          run={scans?.runs[0] ?? null}
          previewItems={(scans?.items ?? [])
            .filter((i) => i.runId === scans?.runs[0]?.id)
            .slice(0, 6)
            .map((i) => ({ id: i.id, name: i.name, thumbUrl: i.thumbUrl }))}
        />
      </div>

      <div className="mt-5 sm:mt-8">
        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" aria-hidden />
            ))}
          </div>
        ) : (
          <DashboardStats
            activeAds={activeRows.length}
            views={views}
            contacts={contacts}
            unread={unread}
          />
        )}
      </div>

      <section className="mt-6 sm:mt-10">
        <h2 className="font-heading text-lg font-semibold text-foreground">{t("account.overview.shortcuts")}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {[
            { to: "/meddelanden" as const, label: t("account.overview.inbox"), desc: unread > 0 ? t("account.overview.unread", { count: unread }) : t("account.overview.noUnread") },
            { to: "/mina-annonser" as const, label: t("account.overview.myAds"), desc: t("account.overview.total", { count: rows.length }) },
            { to: "/mina-bud" as const, label: t("account.overview.bids"), desc: t("account.overview.active", { count: (bids ?? []).length }) },
            { to: "/mina-affarer" as const, label: t("account.overview.deals"), desc: t("account.overview.orders") },
            { to: "/sparade" as const, label: t("account.overview.saved"), desc: t("account.overview.favorites") },
            { to: "/bevakningar" as const, label: t("account.overview.alerts"), desc: t("account.overview.alertsHint") },
            { to: "/mina-prylar" as const, label: t("account.overview.items"), desc: t("account.overview.scanned") },
            { to: "/statistik" as const, label: t("account.overview.stats"), desc: t("account.overview.statsHint") },
          ].map((s) => (
            <Link
              key={s.to}
              to={s.to}
              className="rounded-xl border border-border bg-card p-3 shadow-sm transition-colors hover:border-primary/40 hover:bg-accent/40"
            >
              <span className="block text-sm font-semibold text-foreground">{s.label}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">{s.desc}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-6 sm:mt-10">

        <div className="flex items-center justify-between gap-3">
          <h2 className="font-heading text-lg font-semibold text-foreground">
            {t("account.overview.currentAds")}
          </h2>
          <Link
            to="/mina-annonser"
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
          >
            {t("account.overview.seeAll")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-3">
          {isLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-20 animate-pulse rounded-xl bg-muted" aria-hidden />
              ))}
            </div>
          ) : (
            <DashboardAdList rows={topRows} images={images} />
          )}
        </div>
      </section>

      {(bids ?? []).length > 0 && (
        <section className="mt-6 sm:mt-10">
          <h2 className="font-heading text-lg font-semibold text-foreground">{t("account.overview.yourBids")}</h2>
          <div className="mt-3">
            <DashboardBids rows={bids ?? []} />
          </div>
        </section>
      )}

      <section className="mt-6 sm:mt-10">
        <h2 className="font-heading text-lg font-semibold text-foreground">{t("account.overview.todo")}</h2>
        <div className="mt-3">
          <DashboardTodo items={todos} />
        </div>
      </section>

      <section className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4 shadow-sm sm:mt-10">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Crown className="h-4 w-4 text-primary" />
          <span>
            {t("account.overview.plan")}: <span className="font-semibold text-foreground">{plan.name}</span>
            {left === null
              ? ` · ${t("account.overview.unlimited")}`
              : ` · ${t("account.overview.remaining", { count: left })}`}
          </span>
        </p>
        <div className="flex items-center gap-3">
          <Link to="/min-niva" className="text-sm font-semibold text-foreground hover:underline">
            {t("account.overview.included")}
          </Link>
          {!planAtLeast(currentPlan, "pro") && (
            <Link to="/priser" className="text-sm font-semibold text-primary hover:underline">
              {t("account.overview.compare")}
            </Link>
          )}
        </div>
      </section>

    </main>
  );
}
